import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Scale, Calculator, Sparkles, Truck, Hammer, Leaf, KeyRound, ClipboardCheck, FileSignature, Home, Wrench, ArrowRight, Phone, CheckCircle2, ShieldCheck } from "lucide-react";
import Layout from "@/components/site/Layout";
import PageHero from "@/components/site/PageHero";

const PARTNERS = [
  { icon: Scale, title: "Probate & Estate Attorneys", desc: "Guidance on opening probate, petitioning the court, fiduciary duties, and navigating disputes among heirs.", image: "https://images.pexels.com/photos/5668772/pexels-photo-5668772.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { icon: Calculator, title: "CPAs & Tax Professionals", desc: "Help with the estate's final income tax filings, understanding step-up in basis on inherited property, and any estate tax questions.", image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=80" },
  { icon: Sparkles, title: "Estate Sale & Cleanout Companies", desc: "Specialists who can sort, appraise, sell, donate, or dispose of personal property before a home goes on the market — handled with care and respect for the family.", image: "https://images.pexels.com/photos/7464230/pexels-photo-7464230.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { icon: Truck, title: "Junk Removal & Hauling Services", desc: "Fast, affordable removal of items that won't be sold or kept, to get a property show-ready.", image: "https://customer-assets-rejwkqb3.emergentagent.net/job_probate-luxury-demo/artifacts/t1wsn19h_ChatGPT%20Image%20Aug%2024%2C%202026%2C%2002_51_03%20PM.png" },
  { icon: Hammer, title: "Handyman & Repair Contractors", desc: "Pre-listing repairs, code compliance work, or minor updates that can meaningfully improve a home's sale price.", image: "https://images.pexels.com/photos/5691622/pexels-photo-5691622.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { icon: Leaf, title: "Landscaping & Property Maintenance", desc: "Keeping a vacant or inherited property maintained and presentable while it moves through probate.", image: "https://images.pexels.com/photos/1301856/pexels-photo-1301856.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { icon: KeyRound, title: "Locksmiths", desc: "Securing a property promptly after a loss, including rekeying for personal representatives and heirs.", image: "https://images.pexels.com/photos/279810/pexels-photo-279810.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { icon: Home, title: "Moving Companies", desc: "Support for downsizing and relocating heirs or the surviving family with care and efficiency.", image: "https://images.pexels.com/photos/7464187/pexels-photo-7464187.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { icon: FileSignature, title: "Title & Escrow Companies", desc: "Probate-experienced title and escrow officers who understand court confirmation requirements and the documentation a probate sale demands.", image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=80" },
  { icon: ClipboardCheck, title: "Home Inspectors & Appraisers", desc: "Independent inspections and valuations to support pricing decisions and disclosures.", image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=900&q=80" },
  { icon: Wrench, title: "Notary Services", desc: "Mobile and same-day notary support for the many documents probate administration requires.", image: "https://images.pexels.com/photos/8730998/pexels-photo-8730998.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

const WHY = [
  { title: "Probate experience matters", desc: "A general contractor or attorney who's never touched a probate file can misunderstand court timelines, notice requirements, or fiduciary obligations. Every professional in our network regularly works with estates and understands the rules that apply." },
  { title: "One point of contact", desc: "Instead of researching and vetting each vendor on your own, you get a short list of professionals we already trust — saving you time during a period when time and energy are already stretched thin." },
  { title: "Local to Orange County", desc: "From Huntington Beach to Irvine, Newport Beach to Garden Grove, our referral partners know the local market, local permitting offices, and the Orange County Superior Court probate process firsthand." },
  { title: "No added cost to you", desc: "Referrals are a convenience we offer our clients — you're always free to choose your own provider, and you remain in control of who you hire." },
];

const HOW = [
  { n: "01", t: "Tell us what you need", d: "Whether it's a probate attorney, a cleanout crew, or a contractor, let us know where you are in the process." },
  { n: "02", t: "We make the introduction", d: "We connect you with a trusted professional from our network who's well-suited to your situation." },
  { n: "03", t: "You stay in control", d: "You decide who to hire — our referral simply removes the guesswork of finding someone reliable." },
  { n: "04", t: "We stay involved", d: "As your probate real estate specialist, we coordinate with these professionals throughout the sale so nothing falls through the cracks." },
];

const FAQS = [
  { q: "Do I have to use the professionals you refer me to?", a: "Not at all. Our referrals are a convenience, not a requirement — you're always free to work with whoever you choose." },
  { q: "Is there a cost for the referral?", a: "No. Connecting you with trusted professionals is part of the service we provide as your probate real estate specialist." },
  { q: "What if I already have an attorney or accountant?", a: "That's great — we're happy to coordinate directly with whatever team you already have in place." },
  { q: "Do these professionals work specifically with probate and estate sales?", a: "Yes. Every professional in our network has experience with the unique requirements of probate and trust administration, not just standard real estate transactions." },
];

export default function ReferralServices() {
  return (
    <Layout>
      <PageHero
        breadcrumb="Home / Referral Services"
        overline="Trusted Referral Services for Probate & Estate Sales"
        title="One trusted network. Every professional"
        accentWord="you'll need."
        description={
          <>
            <p>
              Settling an estate rarely involves just one task. Between legal filings, taxes, repairs, and clearing out a lifetime of belongings, an executor or administrator often needs to coordinate with half a dozen different professionals — usually during one of the most stressful periods of their life. That&apos;s where we come in.
            </p>
            <p>
              As a probate real estate specialist serving all of Orange County and Southern California, <span className="font-semibold text-navy-900">Probate Solution OC</span> has spent years building relationships with vetted, probate-experienced professionals across every discipline an estate typically needs. Rather than searching for and screening each one yourself, we connect you directly with people we trust — so you can move through the process with confidence instead of guesswork.
            </p>
          </>
        }
        image="https://images.unsplash.com/photo-1760773766930-2e4efd47f496?w=1600&q=80"
        rightImage="https://images.unsplash.com/photo-1581609836630-9007630f7a7b?w=1200&q=80"
        rightImageAlt="Orange County palm trees at sunset"
      />

      {/* Why Work With Our Network */}
      <section className="py-20 bg-navy-50/40" data-testid="referral-why">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="section-overline">Why Work With Our Network</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Four reasons families <em className="italic text-gold-600">lean on us</em>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {WHY.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                data-testid={`referral-why-${i}`}
                className="rounded-2xl bg-white border border-navy-100 p-7 hover:border-gold-400 hover:shadow-lg transition-all"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gold-500/10 text-gold-600">
                  <CheckCircle2 size={18} />
                </div>
                <h3 className="mt-5 font-heading text-xl text-navy-900">{w.title}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Professionals grid */}
      <section className="py-20 bg-white" data-testid="referral-grid">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="section-overline">Professionals We Can Connect You With</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Eleven specialties — <em className="italic text-gold-600">one call away</em>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PARTNERS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                data-testid={`referral-${i}`}
                className="group rounded-2xl overflow-hidden border border-navy-100 bg-white hover:border-gold-400 hover:shadow-xl transition-all"
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent" />
                  <div className="absolute top-3 left-3 inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/90 backdrop-blur text-gold-600">
                    <p.icon size={18} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl text-navy-900 leading-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-navy-600 leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How the referral process works */}
      <section className="py-20 bg-navy-50/40" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="section-overline">How the Referral Process Works</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Four steps. <em className="italic text-gold-600">Zero pressure</em>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-3xl p-7"
                data-testid={`how-step-${i}`}
              >
                <div className="font-heading text-5xl text-gold-500">{s.n}</div>
                <h3 className="mt-3 font-heading text-xl text-navy-900">{s.t}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white" data-testid="referral-faqs">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <span className="section-overline">Frequently Asked Questions</span>
          <h2 className="mt-3 font-heading text-4xl text-navy-900 leading-tight">
            Straight answers about <em className="italic text-gold-600">our referrals</em>.
          </h2>
          <div className="mt-10 divide-y divide-navy-200/60 border-y border-navy-200/60">
            {FAQS.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`referral-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-heading text-lg text-navy-900 pr-6">{f.q}</h3>
                  <span className="h-8 w-8 rounded-full border border-navy-200 flex items-center justify-center text-navy-700 group-open:bg-gold-500 group-open:text-navy-900 group-open:border-gold-500 transition-all">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-navy-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Let us help you build your team */}
      <section className="py-20 bg-navy-900 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gold-500/15 blur-3xl" />
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 relative text-center">
          <ShieldCheck size={36} className="mx-auto text-gold-400" />
          <h2 className="mt-5 font-heading text-4xl sm:text-5xl text-white leading-tight">
            Let us help you <em className="italic text-gold-400">build your team</em>.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto">
            Navigating a probate real estate sale in Orange County involves more moving pieces than most families expect. You don't have to figure it out — or find the right people — alone.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="tel:+17143913645" data-testid="referral-cta-phone" className="btn-gold group">
              <Phone size={16} className="mr-2" />
              (714) 391-3645
            </a>
            <Link to="/contact" data-testid="referral-cta-contact" className="inline-flex items-center justify-center rounded-full border border-white/20 text-white px-7 py-3.5 text-sm font-medium hover:bg-white hover:text-navy-900 transition-all">
              Request a Referral
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
          <p className="mt-8 text-xs text-white/50 max-w-3xl mx-auto leading-relaxed">
            Probate Solution OC provides referrals as a courtesy to our clients. We do not guarantee the work of any referred professional, and clients are encouraged to do their own due diligence before engaging any service provider.
          </p>
        </div>
      </section>
    </Layout>
  );
}
